import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  UploadFile as UploadFileIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { externalDocumentsService, companiesService, invoicesService, bankTransactionsService } from '../services/api';

const CATEGORIES = [
  'Achat de matériel',
  'Services',
  'Location',
  'Assurance',
  'Téléphonie',
  'Internet',
  'Électricité',
  'Carburant',
  'Fournitures',
  'Logiciels',
  'Autre',
];

const PAYMENT_METHODS = [
  'Virement',
  'Carte bancaire',
  'Chèque',
  'Espèces',
  'Prélèvement',
];

const detectDelimiter = (headerLine) => {
  const semicolons = headerLine.split(';').length;
  const commas = headerLine.split(',').length;
  return semicolons >= commas ? ';' : ',';
};

const parseCSVLine = (line, delimiter) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current);
  return values.map((value) => value.trim());
};

const parseFrenchDate = (value) => {
  if (!value) return null;
  const trimmed = value.trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return new Date(trimmed);
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split('/');
    return new Date(`${year}-${month}-${day}`);
  }

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split('.');
    return new Date(`${year}-${month}-${day}`);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseAmount = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace('€', '')
    .replace(',', '.');
  const amount = parseFloat(normalized);
  return Number.isNaN(amount) ? null : amount;
};

const parseBankCSV = (content) => {
  const lines = content.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) return { headers: [], rows: [], delimiter: ';' };

  const delimiter = detectDelimiter(lines[0]);
  const headers = parseCSVLine(lines[0], delimiter);
  const rows = lines.slice(1).map((line) => parseCSVLine(line, delimiter));

  return { headers, rows, delimiter };
};

const buildTransactionsFromMapping = ({ headers, rows, mapping }) => {
  if (!headers.length || !rows.length) return [];

  const getIndex = (key) => (key ? headers.findIndex((header) => header === key) : -1);

  const dateIndex = getIndex(mapping.date);
  const labelIndex = getIndex(mapping.label);
  const amountIndex = getIndex(mapping.amount);
  const debitIndex = getIndex(mapping.debit);
  const creditIndex = getIndex(mapping.credit);
  const referenceIndex = getIndex(mapping.reference);
  const accountIndex = getIndex(mapping.account);

  return rows.reduce((acc, values) => {
    const dateValue = dateIndex !== -1 ? values[dateIndex] : null;
    const labelValue = labelIndex !== -1 ? values[labelIndex] : null;

    const transactionDate = parseFrenchDate(dateValue);
    const label = labelValue ? String(labelValue).trim() : '';

    let amount = null;
    let direction = 'DEBIT';

    if (amountIndex !== -1) {
      amount = parseAmount(values[amountIndex]);
      if (amount !== null) {
        direction = amount >= 0 ? 'CREDIT' : 'DEBIT';
      }
    } else {
      const debit = debitIndex !== -1 ? parseAmount(values[debitIndex]) : null;
      const credit = creditIndex !== -1 ? parseAmount(values[creditIndex]) : null;

      if (credit !== null && credit !== 0) {
        amount = credit;
        direction = 'CREDIT';
      } else if (debit !== null && debit !== 0) {
        amount = -Math.abs(debit);
        direction = 'DEBIT';
      }
    }

    if (!transactionDate || !label || amount === null) {
      return acc;
    }

    acc.push({
      transactionDate: transactionDate.toISOString(),
      label,
      amount,
      direction,
      currency: 'EUR',
      reference: referenceIndex !== -1 ? values[referenceIndex] : null,
      accountName: accountIndex !== -1 ? values[accountIndex] : null,
      rawData: JSON.stringify(values),
    });

    return acc;
  }, []);
};

function Accounting() {
  const [documents, setDocuments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [csvMappingOpen, setCsvMappingOpen] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState({
    date: '',
    label: '',
    amount: '',
    debit: '',
    credit: '',
    reference: '',
    account: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    isPaid: '',
    dateFrom: '',
    dateTo: '',
  });
  
  const [formData, setFormData] = useState({
    companyId: '',
    supplierName: '',
    category: '',
    documentNumber: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    vatRate: '20',
    paymentMethod: 'Virement',
    isPaid: false,
    paymentDate: '',
    dueDate: '',
    notes: '',
  });

  useEffect(() => {
    loadBankData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [docs, comps, statsData] = await Promise.all([
        externalDocumentsService.getAll(filters),
        companiesService.getAll(),
        externalDocumentsService.getStats(),
      ]);
      setDocuments(docs);
      setCompanies(comps);
      setStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadBankData = async () => {
    try {
      setBankLoading(true);
      const [transactions, invoicesData] = await Promise.all([
        bankTransactionsService.getAll(),
        invoicesService.getAll(),
      ]);
      setBankTransactions(transactions);
      setInvoices(invoicesData);
      setBankError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des transactions:', err);
      setBankError(err.response?.data?.error || 'Erreur lors du chargement des transactions');
    } finally {
      setBankLoading(false);
    }
  };

  const handleOpenDialog = (document = null) => {
    if (document) {
      setEditingDocument(document);
      setFormData({
        companyId: document.companyId || '',
        supplierName: document.supplierName || '',
        category: document.category || '',
        documentNumber: document.documentNumber || '',
        date: document.documentDate
          ? new Date(document.documentDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        amount: document.amount || '',
        vatRate: document.vatRate || '20',
        paymentMethod: document.paymentMethod || 'Virement',
        isPaid: document.isPaid || false,
        paymentDate: document.paymentDate ? new Date(document.paymentDate).toISOString().split('T')[0] : '',
        dueDate: document.dueDate ? new Date(document.dueDate).toISOString().split('T')[0] : '',
        notes: document.notes || '',
      });
    } else {
      setEditingDocument(null);
      setFormData({
        companyId: '',
        supplierName: '',
        category: '',
        documentNumber: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        vatRate: '20',
        paymentMethod: 'Virement',
        isPaid: false,
        paymentDate: '',
        dueDate: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDocument(null);
  };

  const handleSubmit = async () => {
    try {
      const dataToSend = {
        ...formData,
        companyId: formData.companyId || null,
        amount: parseFloat(formData.amount),
        vatRate: parseFloat(formData.vatRate),
        documentDate: formData.date || null,
        paymentDate: formData.paymentDate || null,
        dueDate: formData.dueDate || null,
      };

      if (editingDocument) {
        await externalDocumentsService.update(editingDocument.id, dataToSend);
      } else {
        await externalDocumentsService.create(dataToSend);
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture fournisseur ?')) {
      try {
        await externalDocumentsService.delete(id);
        loadData();
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        setError(err.response?.data?.error || 'Erreur lors de la suppression');
      }
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const formatCurrency = (value) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('fr-FR');
  };

  const findSuggestedInvoice = (transaction) => {
    if (!transaction) return null;
    const amount = Math.abs(transaction.amount);
    const transactionDate = new Date(transaction.transactionDate);

    const candidates = invoices.filter((invoice) =>
      invoice.status !== 'PAID' && Math.abs((invoice.totalTTC || 0) - amount) < 0.01
    );

    const sorted = candidates
      .map((invoice) => ({
        invoice,
        diffDays: Math.abs((new Date(invoice.date) - transactionDate) / 86400000),
      }))
      .sort((a, b) => a.diffDays - b.diffDays);

    return sorted.find((item) => item.diffDays <= 10)?.invoice || sorted[0]?.invoice || null;
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setBankError(null);
    setImportSummary(null);

    try {
      const content = await file.text();
      const parsed = parseBankCSV(content);

      if (!parsed.headers.length || !parsed.rows.length) {
        setBankError('Aucune ligne valide détectée dans le CSV');
        return;
      }

      const lowerHeaders = parsed.headers.map((header) => header.toLowerCase());
      const findHeader = (keys) => {
        const idx = lowerHeaders.findIndex((header) => keys.some((key) => header.includes(key)));
        return idx !== -1 ? parsed.headers[idx] : '';
      };

      setCsvHeaders(parsed.headers);
      setCsvRows(parsed.rows);
      setCsvMapping({
        date: findHeader(['date', 'operation']),
        label: findHeader(['libell', 'label', 'description', 'intitul', 'motif']),
        amount: findHeader(['montant', 'amount', 'eur']),
        debit: findHeader(['debit', 'débit']),
        credit: findHeader(['credit', 'crédit']),
        reference: findHeader(['reference', 'ref', 'num', 'numero', 'n°']),
        account: findHeader(['compte', 'account']),
      });

      setCsvMappingOpen(true);
    } catch (err) {
      console.error('Erreur import CSV:', err);
      setBankError(err.response?.data?.error || 'Erreur lors de l\'import du CSV');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleConfirmMapping = async () => {
    if (!csvMapping.date || !csvMapping.label || (!csvMapping.amount && !csvMapping.debit && !csvMapping.credit)) {
      setBankError('Veuillez mapper au minimum Date, Libellé et Montant (ou Débit/Crédit).');
      return;
    }

    try {
      setImporting(true);
      const parsed = buildTransactionsFromMapping({
        headers: csvHeaders,
        rows: csvRows,
        mapping: csvMapping,
      });

      if (parsed.length === 0) {
        setBankError('Aucune ligne valide après mapping');
        return;
      }

      const response = await bankTransactionsService.importTransactions(parsed);
      setImportSummary(`${response.imported || parsed.length} transactions importées`);
      setCsvMappingOpen(false);
      await loadBankData();
    } catch (err) {
      console.error('Erreur import CSV:', err);
      setBankError(err.response?.data?.error || 'Erreur lors de l\'import du CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleMatch = async (transactionId, invoiceId) => {
    if (!invoiceId) return;
    try {
      await bankTransactionsService.matchTransaction(transactionId, invoiceId);
      await loadBankData();
    } catch (err) {
      console.error('Erreur rapprochement:', err);
      setBankError(err.response?.data?.error || 'Erreur lors du rapprochement');
    }
  };

  const calculateTotalTTC = () => {
    const amount = parseFloat(formData.amount) || 0;
    const vatRate = parseFloat(formData.vatRate) || 0;
    const vatAmount = amount * (vatRate / 100);
    return (amount + vatAmount).toFixed(2);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
          Comptabilité - Factures Fournisseurs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#1F2937',
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          Nouvelle facture
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total dépenses
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
                  {stats.totalExpenses?.toFixed(2) || '0.00'} €
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Payées
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {stats.paidExpenses?.toFixed(2) || '0.00'} €
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En attente
                </Typography>
                <Typography variant="h5" sx={(theme) => ({ fontWeight: 'bold', color: theme.palette.primary.main })}>
                  {stats.unpaidExpenses?.toFixed(2) || '0.00'} €
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Nombre de factures
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {stats.totalCount || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Rapprochement bancaire */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
              Rapprochement bancaire (CSV)
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              component="label"
              disabled={importing}
              sx={{ bgcolor: '#1F2937', '&:hover': { bgcolor: '#374151' } }}
            >
              Importer un CSV
              <input
                type="file"
                hidden
                accept=".csv,text/csv"
                onChange={handleImportFile}
              />
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Formats acceptés : Date, Libellé, Montant (ou colonnes Débit/Crédit). Le système propose un
            rapprochement automatique sur le montant et la date.
          </Typography>

          {importSummary && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportSummary(null)}>
              {importSummary}
            </Alert>
          )}

          {bankError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBankError(null)}>
              {bankError}
            </Alert>
          )}

          {bankLoading ? (
            <Typography>Chargement des transactions...</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Libellé</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Suggestion</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bankTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary">Aucune transaction importée</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bankTransactions.map((tx) => {
                      const suggestedInvoice = findSuggestedInvoice(tx);
                      const isMatched = tx.matchStatus === 'MATCHED';

                      return (
                        <TableRow key={tx.id} hover>
                          <TableCell>{new Date(tx.transactionDate).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>{tx.label}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(tx.amount)}
                          </TableCell>
                          <TableCell>{tx.direction === 'CREDIT' ? 'Crédit' : 'Débit'}</TableCell>
                          <TableCell>
                            {isMatched ? (
                              <Chip label="Rapproché" color="success" size="small" />
                            ) : (
                              <Chip label="À rapprocher" color="warning" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {tx.matchedInvoice ? (
                              <Chip
                                label={`Facture ${tx.matchedInvoice.number}`}
                                color="success"
                                size="small"
                              />
                            ) : suggestedInvoice ? (
                              <Chip
                                label={`Facture ${suggestedInvoice.number}`}
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              <Typography color="textSecondary" variant="caption">
                                Aucune
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {!isMatched && suggestedInvoice ? (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LinkIcon />}
                                onClick={() => handleMatch(tx.id, suggestedInvoice.id)}
                                sx={{ bgcolor: '#1F2937', '&:hover': { bgcolor: '#374151' } }}
                              >
                                Rapprocher
                              </Button>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={filters.category}
                label="Catégorie"
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <MenuItem value="">Toutes</MenuItem>
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Statut paiement</InputLabel>
              <Select
                value={filters.isPaid}
                label="Statut paiement"
                onChange={(e) => handleFilterChange('isPaid', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Payées</MenuItem>
                <MenuItem value="false">En attente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Date début"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Date fin"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau des factures */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Fournisseur</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>N° Document</TableCell>
              <TableCell align="right">Montant HT</TableCell>
              <TableCell align="right">TVA</TableCell>
              <TableCell align="right">Total TTC</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Date paiement</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="textSecondary">Aucune facture fournisseur</Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>{formatDate(doc.documentDate || doc.date)}</TableCell>
                  <TableCell>{doc.supplierName}</TableCell>
                  <TableCell>
                    <Chip label={doc.category} size="small" />
                  </TableCell>
                  <TableCell>{doc.documentNumber}</TableCell>
                  <TableCell align="right">{doc.amount.toFixed(2)} €</TableCell>
                  <TableCell align="right">{doc.vatRate}%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {doc.totalTTC.toFixed(2)} €
                  </TableCell>
                  <TableCell>
                    {doc.isPaid ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Payée"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<PendingIcon />}
                        label="En attente"
                        color="warning"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(doc.paymentDate)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(doc)}
                        sx={{ color: '#1F2937' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(doc.id)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDocument ? 'Modifier la facture fournisseur' : 'Nouvelle facture fournisseur'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Client (optionnel)</InputLabel>
                <Select
                  value={formData.companyId}
                  label="Client (optionnel)"
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nom du fournisseur"
                value={formData.supplierName}
                onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.category}
                  label="Catégorie"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="N° de document"
                value={formData.documentNumber}
                onChange={(e) => setFormData({ ...formData, documentNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date d'échéance"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Montant HT"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                InputProps={{ endAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Taux TVA"
                type="number"
                value={formData.vatRate}
                onChange={(e) => setFormData({ ...formData, vatRate: e.target.value })}
                InputProps={{ endAdornment: '%' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Total TTC"
                value={calculateTotalTTC()}
                InputProps={{ endAdornment: '€', readOnly: true }}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Moyen de paiement</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  label="Moyen de paiement"
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut de paiement</InputLabel>
                <Select
                  value={formData.isPaid}
                  label="Statut de paiement"
                  onChange={(e) => setFormData({ ...formData, isPaid: e.target.value === 'true' })}
                >
                  <MenuItem value={false}>En attente</MenuItem>
                  <MenuItem value={true}>Payée</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.isPaid && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date de paiement"
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#1F2937',
              '&:hover': { bgcolor: '#374151' },
            }}
          >
            {editingDocument ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog mapping CSV */}
      <Dialog open={csvMappingOpen} onClose={() => setCsvMappingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Mapping des colonnes CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Associez les colonnes du CSV aux champs attendus. Les champs requis sont Date, Libellé et Montant
            (ou Débit/Crédit).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Date</InputLabel>
                <Select
                  value={csvMapping.date}
                  label="Date"
                  onChange={(e) => setCsvMapping({ ...csvMapping, date: e.target.value })}
                >
                  {csvHeaders.map((header) => (
                    <MenuItem key={`date-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Libellé</InputLabel>
                <Select
                  value={csvMapping.label}
                  label="Libellé"
                  onChange={(e) => setCsvMapping({ ...csvMapping, label: e.target.value })}
                >
                  {csvHeaders.map((header) => (
                    <MenuItem key={`label-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Montant (unique)</InputLabel>
                <Select
                  value={csvMapping.amount}
                  label="Montant (unique)"
                  onChange={(e) => setCsvMapping({ ...csvMapping, amount: e.target.value })}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem key={`amount-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Débit</InputLabel>
                <Select
                  value={csvMapping.debit}
                  label="Débit"
                  onChange={(e) => setCsvMapping({ ...csvMapping, debit: e.target.value })}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem key={`debit-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Crédit</InputLabel>
                <Select
                  value={csvMapping.credit}
                  label="Crédit"
                  onChange={(e) => setCsvMapping({ ...csvMapping, credit: e.target.value })}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem key={`credit-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Référence</InputLabel>
                <Select
                  value={csvMapping.reference}
                  label="Référence"
                  onChange={(e) => setCsvMapping({ ...csvMapping, reference: e.target.value })}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem key={`ref-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Compte</InputLabel>
                <Select
                  value={csvMapping.account}
                  label="Compte"
                  onChange={(e) => setCsvMapping({ ...csvMapping, account: e.target.value })}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem key={`account-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvMappingOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleConfirmMapping} disabled={importing}>
            Importer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Accounting;
